import { z } from "zod";
import {
	type EmMachine,
	type EmPhysicalMachineSettings,
	type EmMachineRequest,
	type MachineCorrugateTrackWithOffset,
	emMachineSchema,
	emPhysicalMachineSettingsSchema,
	prepareEmMachineData,
} from "@packsize/schemas/machines";

import { packNetAxios } from "@/lib/api-client";

import { baseRoutes } from "../base-routes";

export type EmServiceCommand = "feedforward" | "feedbackwards" | "cleancut";

export interface EmServiceCommandVariables {
	readonly machineId: string;
	readonly trackNumber: number;
	readonly command: EmServiceCommand;
}

export interface ImportEmMachineConfigurationRequest {
	readonly file: File;
	readonly physicalMachineSettings: EmPhysicalMachineSettings;
}

export async function getEmMachines(): Promise<EmMachine[]> {
	const response = await packNetAxios.get(`${baseRoutes.em}/api/v1/machines`);

	if (Array.isArray(response.data) === false) {
		throw new Error("Unable to fetch em machines, response is not a list");
	}

	return z
		.array(emMachineSchema)
		.enrich({ callSite: "getEmMachines - v2" })
		.parseWithTelemetry(response.data.map(prepareEmMachineData));
}

export async function getEmMachine(machineId: string): Promise<EmMachine | null> {
	const response = await packNetAxios.get(`${baseRoutes.em}/api/v1/machines/${machineId}`);

	if (response.status !== 200) {
		return null;
	}

	return emMachineSchema
		.enrich({ callSite: "getEmMachine - v2" })
		.parseWithTelemetry(prepareEmMachineData(response.data));
}

export async function createEmMachine(data: EmMachineRequest): Promise<void> {
	await packNetAxios.post(`${baseRoutes.em}/api/v1/machines`, data);
}

export async function updateEmMachine(tenantId: string, machineId: string, data: EmMachineRequest): Promise<void> {
	await packNetAxios.put(`${baseRoutes.em}/api/v1/machines/${machineId}/configuration`, {
		id: machineId,
		tenantId,
		...data,
	});
}

export async function importEmMachineConfiguration({
	file,
	physicalMachineSettings,
}: ImportEmMachineConfigurationRequest): Promise<EmPhysicalMachineSettings> {
	const formData = new FormData();

	formData.append("formFile", file);
	formData.append("fileName", file.name);
	formData.append("machineSettings", JSON.stringify(physicalMachineSettings));

	const response = await packNetAxios.post(
		`${baseRoutes.em}/api/v1/machines/UploadPhysicalConfigurationFile`,
		formData,
		{
			headers: {
				"Content-Type": "multipart/form-data",
			},
		},
	);

	const parsedSettings = emPhysicalMachineSettingsSchema.safeParse(response.data);

	if (response.status !== 200 || parsedSettings.success === false) {
		throw new Error("Failed to load configuration file");
	}

	return parsedSettings.data;
}

export async function updateEmTracks(machineId: string, tracks: MachineCorrugateTrackWithOffset[]): Promise<void> {
	await packNetAxios.put(
		`${baseRoutes.em}/api/v1/machines/${machineId}/tracks`,
		tracks.map((track) => ({
			number: track.number,
			trackOffset: track.trackOffset,
			loadedCorrugate: {
				id: track.assignedMaterial.id,
				alias: track.assignedMaterial.alias,
				width: track.assignedMaterial.width,
				thickness: track.assignedMaterial.thickness,
				quality: track.assignedMaterial.quality,
			},
		})),
	);
}

export async function executeEmServiceCommand({
	machineId,
	trackNumber,
	command,
}: EmServiceCommandVariables): Promise<void> {
	const response = await packNetAxios.post(
		`${baseRoutes.em}/api/v1/machines/${machineId}/tracks/${trackNumber}/${command}`,
		{},
	);

	if (response.status !== 202) {
		throw new Error(`Failed to execute command ${command} on machine ${machineId} track ${trackNumber}`);
	}
}
